import { logger, isChrome, isSafari, isEdge } from 'utils';
import { Store } from 'stores/type';
import { CustomEvents, EncyclopediaMessage } from 'types';
import { ENCYCLOPEDIA_ID, ENCYCLOPEDIA_ORIGIN } from 'constants/encyclopedia';
import { VERSION } from 'config';
import EventsManager from 'managers/events.manager';
import { isFunction } from 'helpers/types';

import HANDLERS from './handlers';
import COMMANDS from './commands';

export interface Command<K extends keyof CustomEvents, T extends keyof EncyclopediaMessage> {
  trigger: K,
  command: T,
  prepare?: ($store: Store, data: CustomEvents[K]) => EncyclopediaMessage[T],
  callback?: ($store: Store) => void,
}

class EncyclopediaManager {
  private $store!: Store;
  private saved: Array<[string, any]> = [];
  public loaded = false;

  public init(store: Store) {
    this.$store = store;

    window.addEventListener('message', this.onMessageReceived, false);
    this.initCommands();
  }
  private initCommands() {
    COMMANDS.forEach((command) => {
      EventsManager.on(command.trigger as any, (data: any) => {
        let payload = {};
        if (isFunction(command.prepare)) {
          payload = command.prepare(
            this.$store,
            // @ts-ignore
            data,
          );
        }

        this.post(command.command, payload);
        if (isFunction(command.callback)) {
          command.callback(this.$store);
        }
      });
    });
  }

  private onMessageReceived = (event: MessageEvent) => {
    if (!event.data || !event.data.length) return;
    if (__DEV__ && event.origin !== window.location.href) this.log('onMessageReceived', event);

    if (__PLATFORM__ === 'web' && event.origin !== ENCYCLOPEDIA_ORIGIN) return;

    const data = this.parseJSON(event.data);
    if (!data) return;

    this.onMessage(data);
  }
  private onMessage({ command, data }: { command: string, data: any }) {
    this.log('onMessage', command, data);
    if (command === 'ready') {
      this.onReady();
      return;
    }

    if (!HANDLERS.hasOwnProperty(command)) return;

    (HANDLERS as any)[command](data);
  }
  private onReady() {
    this.log('ready');

    this.loaded = true;
    this.saved.forEach((args) => this.post(...args));
    this.saved = [];

    this.post('version', { version: VERSION });

    let browser = '';
    if (isChrome()) browser = 'Chrome';
    if (isSafari()) browser = 'Safari';
    if (isEdge()) browser = 'Edge';
    this.post('userAgent', { browser });
  }

  public post(command: string, data = {}) {
    if (!this.loaded) {
      this.log('post save', command, data);
      this.saved.push([command, data]);
      return;
    }

    if (!this.iframe) return;

    const message = {
      command,
      data,
    };

    this.log('post', command, data);
    this.iframe.postMessage(JSON.stringify(message), '*');
  }

  private parseJSON(data: any) {
    try {
      return JSON.parse(data);
    } catch (e) {
      return null;
    }
  }

  get iframe() {
    const iframe: HTMLIFrameElement | null = document.getElementById(ENCYCLOPEDIA_ID) as any;
    if (!iframe) {
      return null;
    }

    return iframe.contentWindow;
  }

  private log(...messages: any[]) {
    logger('encyclopedia', ...messages);
  }
}

export default new EncyclopediaManager();
